/**
 * Local database service using IndexedDB for offline storage
 * Uses per-user databases for data isolation
 */

import type { ScenarioEvent } from '../core/events/types'
import type { Command } from '../core/commands/types'
import type { Scenario, List, Item, Flow } from '../core/domain/models'
import { getCurrentISOTimestamp } from '../core/utils/timestamp'

const DB_NAME_PREFIX = 'ordelio_db'
const DB_VERSION = 14 // Scenario items metadata store
const DB_METADATA_KEY = 'ordelio_db_metadata'
const MAX_DATABASES = 3
const DB_EXPIRY_DAYS = 30

// Helper to promisify IDBRequest
function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

interface DBMetadata {
  dbName: string
  userId: string
  createdAt: number
  lastAccessedAt: number
}

function getDBName(userId: string): string {
  return `${DB_NAME_PREFIX}_${userId}`
}

interface DBSchema {
  scenarios: {
    key: string
    value: Scenario
  }
  lists: {
    key: string
    value: List
  }
  flows: {
    key: string
    value: Flow
  }
  syncQueue: {
    key: string
    value: SyncQueueItem
  }
  deadLetterQueue: {
    key: string
    value: DeadLetterItem
  }
  syncLog: {
    key: string
    value: SyncLogEntry
  }
  scenarioItems: {
    key: string
    value: ScenarioItemsCollection
  }
  events: {
    key: string
    value: ScenarioEvent
  }
}

export type DeadLetterErrorCode = 'ACCESS_DENIED' | 'NOT_FOUND' | 'CONFLICT' | 'VALIDATION' | 'NETWORK' | 'UNKNOWN'

export interface DeadLetterItem extends SyncQueueItem {
  failureReason: string
  failureTimestamp: number
  canRetry: boolean
  // Denormalized for indexing (also available at command.scenarioId)
  scenarioId?: string
  errorCode?: DeadLetterErrorCode
}

export interface ScenarioItemsCollection {
  scenarioId: string
  items: ScenarioItem[]
  lastSyncedAt: number
}

export interface ScenarioItem {
  id: string
  name: string
  quantity: number
  usageCount: number
  lastUsedAt: string
}

export interface SyncQueueItem {
  commandId: string // Primary key - UUID with cmd_ prefix
  command: Command // The full command object
  timestamp: number // When queued locally
  retries: number
  status: 'pending' | 'processing' | 'processed'
  lastError?: string
  uiMetadata?: {
    entityName?: string
    description?: string // e.g. "Changed status to completed"
  }
}

// Re-export Command type for convenience
export type { Command } from '../core/commands/types'

export interface SyncLogEntry {
  id: string
  timestamp: number
  type: 'create' | 'update' | 'delete' | 'leave'
  entity: 'scenario' | 'list' | 'item'
  entityId: string
  entityName?: string
  actionDescription?: string
  status: 'success' | 'failed' | 'discarded'
  error?: string
  retries: number
}

class LocalDB {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null
  private currentUserId: string | null = null

  async init(userId: string): Promise<void> {
    // If switching users, close current DB
    if (this.currentUserId !== userId && this.db) {
      this.db.close()
      this.db = null
      this.initPromise = null
    }

    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.currentUserId = userId
    const dbName = getDBName(userId)

    // Run cleanup before opening DB
    await this.cleanupOldDatabases(userId)

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        // Update metadata
        this.updateDBMetadata(userId, dbName)
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        const transaction = (event.target as IDBOpenDBRequest).transaction

        // Scenarios store
        if (!db.objectStoreNames.contains('scenarios')) {
          const scenarioStore = db.createObjectStore('scenarios', { keyPath: 'id' })
          scenarioStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        // Lists store
        if (!db.objectStoreNames.contains('lists')) {
          const listStore = db.createObjectStore('lists', { keyPath: 'id' })
          listStore.createIndex('scenarioId', 'scenarioId', { unique: false })
          listStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        // Flows store
        if (!db.objectStoreNames.contains('flows')) {
          const flowStore = db.createObjectStore('flows', { keyPath: 'id' })
          flowStore.createIndex('scenarioId', 'scenarioId', { unique: false })
        }

        // Items store - REMOVED in v13 (Items are embedded in Lists)
        if (db.objectStoreNames.contains('items')) {
            db.deleteObjectStore('items')
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'commandId' })
          syncStore.createIndex('timestamp', 'timestamp', { unique: false })
        } else if (event.oldVersion < 7) {
          // Migrate syncQueue to use commandId as key path
          db.deleteObjectStore('syncQueue')
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'commandId' })
          syncStore.createIndex('timestamp', 'timestamp', { unique: false })
        }

        // Dead letter queue store (failed sync items)
        if (!db.objectStoreNames.contains('deadLetterQueue')) {
          const deadLetterStore = db.createObjectStore('deadLetterQueue', { keyPath: 'commandId' })
          deadLetterStore.createIndex('failureTimestamp', 'failureTimestamp', { unique: false })
          deadLetterStore.createIndex('scenarioId', 'scenarioId', { unique: false })
        } else if (event.oldVersion < 7) {
          // Migrate deadLetterQueue to use commandId as key path
          db.deleteObjectStore('deadLetterQueue')
          const deadLetterStore = db.createObjectStore('deadLetterQueue', { keyPath: 'commandId' })
          deadLetterStore.createIndex('failureTimestamp', 'failureTimestamp', { unique: false })
          deadLetterStore.createIndex('scenarioId', 'scenarioId', { unique: false })
        }

        // Sync log store
        if (!db.objectStoreNames.contains('syncLog')) {
          const logStore = db.createObjectStore('syncLog', { keyPath: 'id' })
          logStore.createIndex('timestamp', 'timestamp', { unique: false })
          logStore.createIndex('status', 'status', { unique: false })
        }

        // Scenario items store (shadow metadata cache)
        if (!db.objectStoreNames.contains('scenarioItems')) {
          const scenarioItemsStore = db.createObjectStore('scenarioItems', { keyPath: 'scenarioId' })
          scenarioItemsStore.createIndex('lastSyncedAt', 'lastSyncedAt', { unique: false })
        }

        // Events store (event sourcing)
        if (!db.objectStoreNames.contains('events')) {
          const eventsStore = db.createObjectStore('events', { keyPath: 'eventId' })
          eventsStore.createIndex('scenarioId', 'scenarioId', { unique: false })
          eventsStore.createIndex('sequenceNumber', ['scenarioId', 'sequenceNumber'], { unique: true })
          eventsStore.createIndex('serverTimestamp', 'serverTimestamp', { unique: false })
        }

        // v8 migration: remove deprecated Scenario.isOwner and normalize role to 'owner' | 'editor'
        if (event.oldVersion < 8 && transaction && db.objectStoreNames.contains('scenarios')) {
          const store = transaction.objectStore('scenarios')
          const getAllRequest = store.getAll()
          getAllRequest.onsuccess = () => {
            const scenarios = getAllRequest.result as any[]
            for (const scenario of scenarios) {
              if (!scenario || typeof scenario !== 'object') continue

              // Remove deprecated field
              if ('isOwner' in scenario) {
                delete scenario.isOwner
              }

              // Normalize role
              const roleCandidate = String(scenario.role || '').toLowerCase()
              scenario.role = roleCandidate === 'owner' ? 'owner' : 'editor'

              store.put(scenario)
            }
          }
        }

        // No legacy migrations.
      }
    })

    return this.initPromise
  }

  private async getStore(storeName: keyof DBSchema, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
    if (!this.currentUserId) {
      throw new Error('LocalDB not initialized with userId. Call init(userId) first.')
    }
    await this.init(this.currentUserId)
    const transaction = this.db!.transaction(storeName, mode)
    return transaction.objectStore(storeName)
  }

  /**
   * Create a transaction spanning multiple stores for atomic operations
   * This ensures data + sync queue updates happen atomically
   */
  private async getTransaction(storeNames: (keyof DBSchema)[], mode: IDBTransactionMode = 'readwrite'): Promise<IDBTransaction> {
    if (!this.currentUserId) {
      throw new Error('LocalDB not initialized with userId. Call init(userId) first.')
    }
    await this.init(this.currentUserId)
    return this.db!.transaction(storeNames as string[], mode)
  }

  /**
   * Create a transaction for event processing with specified stores
   * Stores are declared by the event handler (no hardcoded logic)
   */
  async createEventTransactionWithStores(storeNames: string[]): Promise<IDBTransaction> {
    return this.getTransaction(storeNames as (keyof DBSchema)[], 'readwrite')
  }

  /**
   * Remove command from sync queue within a transaction
   */
  async removeCommandInTransaction(transaction: IDBTransaction, commandId: string): Promise<void> {
    if (!commandId) return
    
    const queueStore = transaction.objectStore('syncQueue')
    
    return new Promise((resolve, reject) => {
      // commandId is now the primary key
      const deleteRequest = queueStore.delete(commandId)
      deleteRequest.onsuccess = () => resolve()
      deleteRequest.onerror = () => reject(deleteRequest.error)
    })
  }

  /**
   * Update scenario sequenceNumber within a transaction
   */
  async updateScenarioSequenceInTransaction(transaction: IDBTransaction, scenarioId: string, sequenceNumber: number): Promise<void> {
    const scenarioStore = transaction.objectStore('scenarios')
    
    return new Promise((resolve, reject) => {
      const getRequest = scenarioStore.get(scenarioId)
      getRequest.onsuccess = () => {
        const scenario = getRequest.result
        if (scenario) {
          scenario.sequenceNumber = sequenceNumber
          const putRequest = scenarioStore.put(scenario)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          resolve() // Scenario not found
        }
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  // Scenarios
  async getScenarios(): Promise<Scenario[]> {
    const store = await this.getStore('scenarios')
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getScenario(id: string): Promise<Scenario | undefined> {
    const store = await this.getStore('scenarios')
    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async saveScenario(scenario: Scenario): Promise<void> {
    const store = await this.getStore('scenarios', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.put({ ...scenario, updatedAt: getCurrentISOTimestamp() })
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Special helper for Scenario Creation ONLY
   * Scenarios are root entities. For them to appear in the dashboard immediately (optimistically),
   * they must exist in the persistent store, as the dashboard does not replay SyncQueue commands.
   * 
   * Do NOT use for updates (Rename/Delete) - use addToSyncQueue directly and let MemoryStore handle optimistic state.
   */
  async saveScenarioAndQueue(scenario: Scenario, command?: Command): Promise<void> {
    const transaction = await this.getTransaction(['scenarios', 'syncQueue'], 'readwrite')
    
    return new Promise((resolve, reject) => {
      const scenarioStore = transaction.objectStore('scenarios')
      const scenarioWithTimestamp = { ...scenario, updatedAt: getCurrentISOTimestamp() }
      console.log('[client.ts] Saving scenario to IndexedDB:', scenarioWithTimestamp)
      console.log('[client.ts] Has id field?', 'id' in scenarioWithTimestamp, scenarioWithTimestamp.id)
      const scenarioRequest = scenarioStore.put(scenarioWithTimestamp)
      
      scenarioRequest.onerror = () => reject(scenarioRequest.error)
      
      if (command) {
        scenarioRequest.onsuccess = () => {
          const queueStore = transaction.objectStore('syncQueue')
          const queueItem: SyncQueueItem = {
            commandId: command.commandId,
            command,
            timestamp: Date.now(),
            retries: 0,
            status: 'pending'
          }
          const queueRequest = queueStore.put(queueItem)
          queueRequest.onerror = () => reject(queueRequest.error)
        }
      }
      
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
  }

  async deleteScenario(id: string): Promise<void> {
    // Delete all lists for this scenario
    const lists = await this.getLists(id)
    for (const list of lists) {
      // Delete all items in each list
      const items = await this.getListItems(list.id)
      for (const item of items) {
        await this.deleteListItem(item.id)
      }
      // Delete the list
      await this.deleteList(list.id)
    }

    // Delete scenario items metadata
    await this.clearScenarioItems(id)
    
    // Delete the scenario header
    const store = await this.getStore('scenarios', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }


  // Lists
  async getLists(scenarioId: string): Promise<List[]> {
    const store = await this.getStore('lists')
    const index = store.index('scenarioId')
    return new Promise((resolve, reject) => {
      const request = index.getAll(scenarioId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getList(id: string): Promise<List | undefined> {
    const store = await this.getStore('lists')
    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async saveList(list: List): Promise<void> {
    const store = await this.getStore('lists', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.put({ ...list, updatedAt: getCurrentISOTimestamp() })
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }


  async deleteList(id: string): Promise<void> {
    const store = await this.getStore('lists', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }


  // Flows
  async getFlows(scenarioId: string): Promise<Flow[]> {
    const store = await this.getStore('flows')
    const index = store.index('scenarioId')
    return new Promise((resolve, reject) => {
      const request = index.getAll(scenarioId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async saveFlow(flow: Flow): Promise<void> {
    const store = await this.getStore('flows', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.put(flow)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async deleteFlow(id: string): Promise<void> {
    const store = await this.getStore('flows', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // List Items
  // In v11+ Items are embedded in Lists. Queries must extract them.

  async getListItems(listId: string): Promise<Item[]> {
    const list = await this.getList(listId)
    return list?.items ? Object.values(list.items) : []
  }

  async getListItem(id: string): Promise<Item | undefined> {
    // This is expensive now (scan all lists), but rarely used.
    // If performance is critical, pass listId or scenarioId.
    // For now, iterate all lists in active scenarios? Too slow.
    // Assuming we have context usually. 
    // If we only have ItemId (e.g. from SyncQueue enrichment without context), we might fail or need a global scan.
    // Let's implement a scan of all lists in current DB.
    const store = await this.getStore('lists')
    return new Promise((resolve, reject) => {
      const request = store.openCursor()
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor) {
          const list = cursor.value as List
          const item = list.items?.[id]
          if (item) {
             resolve(item)
             return
          }
          cursor.continue()
        } else {
          resolve(undefined)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  async saveListItem(item: Item): Promise<void> {
    const store = await this.getStore('lists', 'readwrite')
    return new Promise((resolve, reject) => {
        const getRequest = store.get(item.listId)
        getRequest.onsuccess = () => {
            const list = getRequest.result as List
            if (!list) {
                // Consistency check failed: List does not exist.
                // We drop the item update to avoid ghost items.
                console.warn(`[LocalDB] Cannot save item ${item.id} - parent list ${item.listId} not found.`)
                resolve()
                return
            }
            
            // Append or update in dictionary
            const items = list.items || {}
            items[item.id] = { ...item, updatedAt: getCurrentISOTimestamp() }
            list.items = items
            
            const putRequest = store.put(list)
            putRequest.onsuccess = () => resolve()
            putRequest.onerror = () => reject(putRequest.error)
        }
        getRequest.onerror = () => reject(getRequest.error)
    })
  }


  async deleteListItem(itemId: string): Promise<void> {
    const transaction = await this.getTransaction(['lists'], 'readwrite')
    const store = transaction.objectStore('lists')
    
    return new Promise((resolve, reject) => {
        const cursorRequest = store.openCursor()
        cursorRequest.onsuccess = (e) => {
            const cursor = (e.target as IDBRequest).result
            if (cursor) {
                const list = cursor.value as List
                const items = list.items || {}
                if (items[itemId]) {
                    const { [itemId]: _deleted, ...remaining } = items
                    list.items = remaining
                    cursor.update(list)
                    resolve()
                    return
                }
                cursor.continue()
            } else {
                resolve()
            }
        }
        cursorRequest.onerror = () => reject(cursorRequest.error)
    })
  }
      

  // Sync Queue
  async addToSyncQueue(
    command: Command, 
    uiMetadata?: { entityName?: string, description?: string }
  ): Promise<void> {
    const store = await this.getStore('syncQueue', 'readwrite')
    const queueItem: SyncQueueItem = {
      commandId: command.commandId,
      command,
      timestamp: Date.now(),
      retries: 0,
      status: 'pending',
      uiMetadata
    }
    return new Promise((resolve, reject) => {
      const request = store.put(queueItem)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Update command status in sync queue
   */
  async updateCommandStatus(commandId: string, status: 'pending' | 'processing' | 'processed'): Promise<void> {
    const store = await this.getStore('syncQueue', 'readwrite')
    
    // Get the item by commandId (which is now the primary key)
    const item = await new Promise<SyncQueueItem | undefined>((resolve, reject) => {
      const request = store.get(commandId)
      request.onsuccess = () => resolve(request.result as SyncQueueItem | undefined)
      request.onerror = () => reject(request.error)
    })
    
    if (!item) {
      // Common race condition: EventProcessor likely already processed and deleted the command
      // when handling the incoming SignalR event. This is expected behavior for fast networks.
      // logger.debug(`[LocalDB] Command ${commandId} not found in sync queue (already processed)`)
      return
    }
    
    item.status = status
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(item)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const store = await this.getStore('syncQueue')
    const index = store.index('timestamp')
    return new Promise((resolve, reject) => {
      const request = index.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async removeSyncQueueItem(commandId: string): Promise<void> {
    const store = await this.getStore('syncQueue', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.delete(commandId)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async updateSyncQueueItem(item: SyncQueueItem): Promise<void> {
    const store = await this.getStore('syncQueue', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.put(item)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // Sync Log operations
  async addSyncLogEntry(entry: Omit<SyncLogEntry, 'id'>): Promise<void> {
    const store = await this.getStore('syncLog', 'readwrite')
    const logEntry: SyncLogEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }
    return new Promise((resolve, reject) => {
      const request = store.put(logEntry)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getSyncLog(limit?: number): Promise<SyncLogEntry[]> {
    const store = await this.getStore('syncLog')
    const index = store.index('timestamp')
    return new Promise((resolve, reject) => {
      const request = index.openCursor(null, 'prev') // Most recent first
      const results: SyncLogEntry[] = []
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor && (!limit || results.length < limit)) {
          results.push(cursor.value)
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  // Scenario Items operations (shadow metadata cache)
  async getScenarioItems(scenarioId: string): Promise<ScenarioItemsCollection | null> {
    const store = await this.getStore('scenarioItems')
    return new Promise((resolve, reject) => {
      const request = store.get(scenarioId)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  }

  async saveScenarioItems(scenarioId: string, items: ScenarioItem[]): Promise<void> {
    const store = await this.getStore('scenarioItems', 'readwrite')
    const data: ScenarioItemsCollection = {
      scenarioId,
      items,
      lastSyncedAt: Date.now()
    }
    return new Promise((resolve, reject) => {
      const request = store.put(data)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearScenarioItems(scenarioId: string): Promise<void> {
    const store = await this.getStore('scenarioItems', 'readwrite')
    return new Promise((resolve, reject) => {
      const request = store.delete(scenarioId)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // Clear all local data across stores (use when switching users or logging out)
  async clearAllData(): Promise<void> {
    const stores: (keyof DBSchema)[] = ['scenarios', 'lists', 'syncQueue', 'syncLog', 'scenarioItems', 'events']
    if (!this.currentUserId) return
    await this.init(this.currentUserId)
    await Promise.all(stores.map(storeName => new Promise<void>(async (resolve, reject) => {
      try {
        const store = await this.getStore(storeName, 'readwrite')
        const request = store.clear()
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err as any)
      }
    })))
  }

  private getDBMetadata(): DBMetadata[] {
    try {
      if (typeof localStorage === 'undefined') return []
      const stored = localStorage.getItem(DB_METADATA_KEY)
      if (!stored) return []
      return JSON.parse(stored)
    } catch {
      return []
    }
  }

  private saveDBMetadata(metadata: DBMetadata[]): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(DB_METADATA_KEY, JSON.stringify(metadata))
      }
    } catch {
      // Ignore storage errors
    }
  }

  private updateDBMetadata(userId: string, dbName: string): void {
    const metadata = this.getDBMetadata()
    const existing = metadata.find(m => m.userId === userId)
    
    if (existing) {
      existing.lastAccessedAt = Date.now()
    } else {
      metadata.push({
        dbName,
        userId,
        createdAt: Date.now(),
        lastAccessedAt: Date.now()
      })
    }
    
    this.saveDBMetadata(metadata)
  }

  private async cleanupOldDatabases(currentUserId: string): Promise<void> {
    const metadata = this.getDBMetadata()
    const now = Date.now()
    const expiryMs = DB_EXPIRY_DAYS * 24 * 60 * 60 * 1000
    const databasesToDelete: string[] = []

    // Find expired databases
    for (const meta of metadata) {
      if (meta.userId !== currentUserId && now - meta.lastAccessedAt > expiryMs) {
        databasesToDelete.push(meta.dbName)
      }
    }

    // If we have more than MAX_DATABASES, delete oldest ones (excluding current user)
    const otherUserDBs = metadata
      .filter(m => m.userId !== currentUserId)
      .sort((a, b) => a.lastAccessedAt - b.lastAccessedAt)
    
    const totalDBs = metadata.length
    if (totalDBs > MAX_DATABASES) {
      const toRemove = totalDBs - MAX_DATABASES
      for (let i = 0; i < toRemove && i < otherUserDBs.length; i++) {
        if (!databasesToDelete.includes(otherUserDBs[i].dbName)) {
          databasesToDelete.push(otherUserDBs[i].dbName)
        }
      }
    }

    // Delete databases
    for (const dbName of databasesToDelete) {
      try {
        await new Promise<void>((resolve, reject) => {
          const request = indexedDB.deleteDatabase(dbName)
          request.onsuccess = () => {
            console.log(`Deleted old database: ${dbName}`)
            resolve()
          }
          request.onerror = () => reject(request.error)
          request.onblocked = () => {
            console.warn(`Delete blocked for database: ${dbName}`)
            resolve() // Continue anyway
          }
        })
      } catch (error) {
        console.error(`Failed to delete database ${dbName}:`, error)
      }
    }

    // Update metadata to remove deleted databases
    const updatedMetadata = metadata.filter(m => !databasesToDelete.includes(m.dbName))
    this.saveDBMetadata(updatedMetadata)
  }

  // Event store methods

  async saveEvent(event: ScenarioEvent): Promise<void> {
    if (!this.db) throw new Error('DB not initialized')
    const tx = this.db.transaction(['events'], 'readwrite')
    const store = tx.objectStore('events')
    
    // Use 'add' instead of 'put' to ensure uniqueness.
    // 'add' throws ConstraintError if key exists, allowing EventProcessor to skip processing.
    await promisifyRequest(store.add(event))
  }

  async getEvent(eventId: string): Promise<ScenarioEvent | null> {
    if (!this.db) throw new Error('DB not initialized')
    const tx = this.db.transaction(['events'], 'readonly')
    const store = tx.objectStore('events')
    const result = await promisifyRequest(store.get(eventId))
    return result || null
  }

  async getEventsByScenario(scenarioId: string, limit?: number): Promise<ScenarioEvent[]> {
    if (!this.db) throw new Error('DB not initialized')
    const tx = this.db.transaction(['events'], 'readonly')
    const store = tx.objectStore('events')
    const index = store.index('scenarioId')
    const results: ScenarioEvent[] = []
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(scenarioId), 'prev') // Most recent first
      request.onsuccess = () => {
        const cursor = request.result
        if (cursor && (!limit || results.length < limit)) {
          results.push(cursor.value)
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  // Dead letter queue methods

  /**
   * Atomically move an item from sync queue to dead letter queue
   * If either operation fails, both are rolled back to prevent data loss
   */
  async moveToDeadLetterQueue(item: SyncQueueItem, failureReason: string, canRetry: boolean, errorCode?: DeadLetterErrorCode): Promise<void> {
    if (!this.db) throw new Error('DB not initialized')
    
    const deadLetterItem: DeadLetterItem = {
      ...item,
      failureReason,
      failureTimestamp: Date.now(),
      canRetry,
      // Denormalize scenarioId at top level for indexing
      scenarioId: item.command.scenarioId,
      errorCode
    }
    
    // ATOMIC: Both operations in single transaction
    // If stores don't exist, transaction will throw - this surfaces bugs rather than hiding them
    const tx = this.db.transaction(['syncQueue', 'deadLetterQueue'], 'readwrite')
    
    try {
      // Remove from sync queue (commandId is the primary key)
      await promisifyRequest(tx.objectStore('syncQueue').delete(item.commandId))
      
      // Add to dead letter queue
      await promisifyRequest(tx.objectStore('deadLetterQueue').put(deadLetterItem))
      
      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(new Error('Transaction aborted'))
      })
    } catch (error) {
      // Transaction will auto-rollback on error
      console.error('Failed to move item to dead letter queue:', error)
      throw error
    }
  }

  async addToDeadLetterQueue(item: SyncQueueItem, failureReason: string, canRetry: boolean, errorCode?: DeadLetterErrorCode): Promise<void> {
    if (!this.db) throw new Error('DB not initialized')
    
    const deadLetterItem: DeadLetterItem = {
      ...item,
      failureReason,
      failureTimestamp: Date.now(),
      canRetry,
      // Denormalize scenarioId at top level for indexing
      scenarioId: item.command.scenarioId,
      errorCode,
      // Preserve lastError from the original item, fallback to failureReason
      lastError: item.lastError || failureReason
    }
    
    const tx = this.db.transaction(['deadLetterQueue'], 'readwrite')
    const store = tx.objectStore('deadLetterQueue')
    await promisifyRequest(store.put(deadLetterItem))
  }

  async getDeadLetterQueue(): Promise<DeadLetterItem[]> {
    if (!this.db) throw new Error('DB not initialized')
    
    const tx = this.db.transaction(['deadLetterQueue'], 'readonly')
    const store = tx.objectStore('deadLetterQueue')
    const index = store.index('failureTimestamp')
    const results: DeadLetterItem[] = []
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(null, 'prev') // Most recent first
      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          results.push(cursor.value)
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  async getDeadLetterQueueForScenario(scenarioId: string): Promise<DeadLetterItem[]> {
    if (!this.db) throw new Error('DB not initialized')
    
    const tx = this.db.transaction(['deadLetterQueue'], 'readonly')
    const store = tx.objectStore('deadLetterQueue')
    const index = store.index('scenarioId')
    const results: DeadLetterItem[] = []
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(scenarioId), 'prev')
      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          results.push(cursor.value)
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Atomically discard a dead letter item by logging it and removing it
   * Both operations happen in a single transaction to prevent inconsistencies
   */
  async discardDeadLetterItem(item: DeadLetterItem, logEntry: SyncLogEntry): Promise<void> {
    if (!this.db) throw new Error('DB not initialized')
    
    // ATOMIC: Log and remove in single transaction
    const tx = this.db.transaction(['syncLog', 'deadLetterQueue'], 'readwrite')
    
    try {
      // Add to sync log
      await promisifyRequest(tx.objectStore('syncLog').add(logEntry))
      
      // Remove from dead letter queue (commandId is the primary key)
      await promisifyRequest(tx.objectStore('deadLetterQueue').delete(item.commandId))
      
      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(new Error('Transaction aborted'))
      })
    } catch (error) {
      // Transaction will auto-rollback on error
      console.error('Failed to discard dead letter item:', error)
      throw error
    }
  }

  /**
   * Atomically move an item from dead letter queue back to sync queue for retry
   * Uses single transaction to ensure item is never lost
   */
  async retryDeadLetterItem(id: string): Promise<SyncQueueItem | null> {
    if (!this.db) throw new Error('DB not initialized')
    
    // ATOMIC: Read, delete, and re-add in single transaction
    const tx = this.db.transaction(['deadLetterQueue', 'syncQueue'], 'readwrite')
    
    try {
      // Get the item
      const deadLetterStore = tx.objectStore('deadLetterQueue')
      const item = await promisifyRequest(deadLetterStore.get(id))
      
      if (!item) {
        return null
      }
      
      // Remove dead letter queue specific fields
      const { failureReason, failureTimestamp, canRetry, ...syncItem } = item
      
      // Remove from dead letter queue
      await promisifyRequest(deadLetterStore.delete(id))
      
      // Add back to sync queue with reset retry count
      const syncStore = tx.objectStore('syncQueue')
      await promisifyRequest(syncStore.put({
        ...syncItem,
        retries: 0, // Reset retry count
        lastError: undefined, // Clear previous error
        nextRetry: Date.now() // Retry immediately
      }))
      
      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(new Error('Transaction aborted'))
      })
      
      return syncItem as SyncQueueItem
    } catch (error) {
      // Transaction will auto-rollback on error
      console.error('Failed to retry dead letter item:', error)
      throw error
    }
  }

  /**
   * Get set of scenario IDs that have failing items in the DLQ
   * Used to pause sync for those scenarios to preserve FIFO
   */
  async getBlockedScenarioIds(): Promise<Set<string>> {
    const store = await this.getStore('deadLetterQueue')
    return new Promise((resolve, reject) => {
      const blocked = new Set<string>()
      const request = store.getAll()
      request.onsuccess = () => {
        const items = request.result as DeadLetterItem[]
        for (const item of items) {
          if (item.command?.scenarioId) {
            blocked.add(item.command.scenarioId)
          }
        }
        resolve(blocked)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get count of related commands for a scenario
   * Returns count of commands in both sync queue and dead letter queue
   * Used to warn user before cascading discard
   */
  async getRelatedCommandCount(scenarioId: string): Promise<{ syncQueueCount: number, deadLetterCount: number }> {
    if (!this.db) throw new Error('DB not initialized')
    
    // Count in sync queue
    const syncStore = await this.getStore('syncQueue')
    const syncQueue = await new Promise<SyncQueueItem[]>((resolve, reject) => {
      const request = syncStore.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    const syncQueueCount = syncQueue.filter(item => item.command.scenarioId === scenarioId).length
    
    // Count in dead letter queue
    const dlqItems = await this.getDeadLetterQueueForScenario(scenarioId)
    
    return {
      syncQueueCount,
      deadLetterCount: dlqItems.length
    }
  }

  /**
   * Discard a dead letter item and optionally all related commands for the same scenario
   * When discarding, also discards commands in sync queue to maintain consistency
   * All operations are logged to sync log
   */
  async discardDeadLetterItemWithRelated(
    item: DeadLetterItem, 
    logEntry: SyncLogEntry,
    discardRelated: boolean
  ): Promise<{ discardedCount: number }> {
    if (!this.db) throw new Error('DB not initialized')
    
    const scenarioId = item.command.scenarioId
    let discardedCount = 0
    
    if (!discardRelated) {
      // Just discard the single item
      await this.discardDeadLetterItem(item, logEntry)
      return { discardedCount: 1 }
    }
    
    // Get all related items to discard
    const syncQueue = await this.getSyncQueue()
    const relatedSyncItems = syncQueue.filter(i => i.command.scenarioId === scenarioId)
    const relatedDLQItems = await this.getDeadLetterQueueForScenario(scenarioId)
    
    // ATOMIC: Discard all related items in single transaction
    const tx = this.db.transaction(['syncQueue', 'deadLetterQueue', 'syncLog'], 'readwrite')
    
    try {
      const syncQueueStore = tx.objectStore('syncQueue')
      const dlqStore = tx.objectStore('deadLetterQueue')
      const syncLogStore = tx.objectStore('syncLog')
      
      // Log and remove each sync queue item
      for (const syncItem of relatedSyncItems) {
        const syncLogEntry: SyncLogEntry = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: syncItem.command.commandType.startsWith('Create') ? 'create' : 
                syncItem.command.commandType.startsWith('Delete') ? 'delete' : 'update',
          entity: syncItem.command.commandType.includes('Item') ? 'item' :
                  syncItem.command.commandType.includes('List') ? 'list' : 'scenario',
          entityId: syncItem.commandId,
          entityName: syncItem.uiMetadata?.entityName || syncItem.commandId,
          status: 'discarded',
          actionDescription: `Discarded as related to failed command: ${item.failureReason}`,
          retries: syncItem.retries || 0
        }
        await promisifyRequest(syncLogStore.add(syncLogEntry))
        await promisifyRequest(syncQueueStore.delete(syncItem.commandId))
        discardedCount++
      }
      
      // Log and remove each dead letter item
      for (const dlqItem of relatedDLQItems) {
        const dlqLogEntry: SyncLogEntry = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: dlqItem.command.commandType.startsWith('Create') ? 'create' : 
                dlqItem.command.commandType.startsWith('Delete') ? 'delete' : 'update',
          entity: dlqItem.command.commandType.includes('Item') ? 'item' :
                  dlqItem.command.commandType.includes('List') ? 'list' : 'scenario',
          entityId: dlqItem.commandId,
          entityName: dlqItem.uiMetadata?.entityName || dlqItem.commandId,
          status: 'discarded',
          actionDescription: dlqItem.commandId === item.commandId 
            ? 'User discarded failed command'
            : `Discarded as related to failed command: ${item.failureReason}`,
          retries: dlqItem.retries || 0
        }
        await promisifyRequest(syncLogStore.add(dlqLogEntry))
        await promisifyRequest(dlqStore.delete(dlqItem.commandId))
        discardedCount++
      }
      
      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(new Error('Transaction aborted'))
      })
      
      return { discardedCount }
    } catch (error) {
      console.error('Failed to discard dead letter item with related:', error)
      throw error
    }
  }

  /**
   * Atomically apply a scenario snapshot with recent history events
   * This method:
   * 1. Deletes ALL scenario data (header, lists, items, flows, metadata, events)
   * 2. Writes the snapshot state
   * 3. Writes recent-history events
   * All in a SINGLE transaction to ensure atomicity
   * 
   * @param scenarioId - The scenario ID
   * @param snapshot - The snapshot to apply
   * @param historyEvents - Recent events for history display (not applied to state)
   * @param userRole - The current user's role for this scenario
   */
  async applyScenarioSnapshotAtomic(
    scenarioId: string,
    snapshot: {
      scenarioId: string
      header: any
      lists: Array<{
        id: string
        name: string
        description?: string
        createdAt: string
        updatedAt: string
        isDeleted: boolean
        items?: Record<string, any>
      }>
      flows: any[]
      metadata: any
      latestSequenceNumber: number
    },
    historyEvents: any[],
    userRole: 'owner' | 'editor' = 'editor'
  ): Promise<void> {
    if (!this.db) throw new Error('DB not initialized')

    // Create single transaction spanning all stores
    const tx = this.db.transaction(
      ['scenarios', 'lists', 'flows', 'scenarioItems', 'events'],
      'readwrite'
    )

    try {
      const scenariosStore = tx.objectStore('scenarios')
      const listsStore = tx.objectStore('lists')
      const flowsStore = tx.objectStore('flows')
      const scenarioItemsStore = tx.objectStore('scenarioItems')
      const eventsStore = tx.objectStore('events')

      // Step 1: Delete all existing scenario data
      
      // Delete scenario header
      await promisifyRequest(scenariosStore.delete(scenarioId))

      // Delete all lists for this scenario
      const listsIndex = listsStore.index('scenarioId')
      const listsToDelete = await promisifyRequest(listsIndex.getAllKeys(scenarioId))
      for (const listKey of listsToDelete) {
        await promisifyRequest(listsStore.delete(listKey))
      }

      // Delete all flows for this scenario
      const flowsIndex = flowsStore.index('scenarioId')
      const flowsToDelete = await promisifyRequest(flowsIndex.getAllKeys(scenarioId))
      for (const flowKey of flowsToDelete) {
        await promisifyRequest(flowsStore.delete(flowKey))
      }

      // Delete scenario items metadata
      await promisifyRequest(scenarioItemsStore.delete(scenarioId))

      // Delete all events for this scenario
      const eventsIndex = eventsStore.index('scenarioId')
      const eventsToDelete = await promisifyRequest(eventsIndex.getAllKeys(scenarioId))
      for (const eventKey of eventsToDelete) {
        await promisifyRequest(eventsStore.delete(eventKey))
      }

      // Step 2: Write snapshot state

      // Write scenario header
      await promisifyRequest(scenariosStore.put({
        id: snapshot.scenarioId,
        name: snapshot.header.name,
        description: snapshot.header.description,
        createdAt: snapshot.header.createdAt,
        updatedAt: snapshot.header.updatedAt,
        sequenceNumber: snapshot.latestSequenceNumber,
        isDeleted: snapshot.header.isDeleted || false,
        role: userRole
      }))

      // Write lists (items are embedded in Lists in v11+)
      for (const list of snapshot.lists) {
        const normalizedItems = list.items
          ? Object.fromEntries(
              Object.entries(list.items).map(([itemKey, raw]) => {
                const rawItem = raw as any
                const id = String(rawItem?.id ?? itemKey ?? '').trim()
                return [
                  id,
                  {
                    ...rawItem,
                    id,
                    listId: list.id,
                    scenarioId: snapshot.scenarioId
                  }
                ]
              })
            )
          : undefined

        await promisifyRequest(
          listsStore.put({
            ...list,
            scenarioId: snapshot.scenarioId,
            items: normalizedItems
          })
        )
      }

      // Write flows
      if (snapshot.flows && Array.isArray(snapshot.flows)) {
        for (const flow of snapshot.flows) {
          await promisifyRequest(flowsStore.put({
            ...flow,
            scenarioId: snapshot.scenarioId
          }))
        }
      }

      // Write scenario items metadata
      if (snapshot.metadata && snapshot.metadata.items) {
        const scenarioItemsArray = Object.entries(snapshot.metadata.items).map(([itemId, s]: any) => ({
          id: String(itemId || '').trim(),
          name: String(s?.displayName ?? s?.name ?? ''),
          quantity: typeof s?.quantity === 'number' && s.quantity >= 1 ? s.quantity : 1,
          usageCount: s?.usageCount || 0,
          lastUsedAt: s?.lastUsedAt || new Date().toISOString()
        }))

        if (scenarioItemsArray.length > 0) {
          await promisifyRequest(scenarioItemsStore.put({
            scenarioId: snapshot.scenarioId,
            items: scenarioItemsArray,
            lastSyncedAt: Date.now()
          }))
        }
      }

      // Step 3: Write recent-history events
      for (const event of historyEvents) {
        await promisifyRequest(eventsStore.put({
          eventId: event.eventId,
          sequenceNumber: event.sequenceNumber,
          originId: event.originId,
          commandId: event.commandId,
          serverTimestamp: event.serverTimestamp,
          clientTimestamp: event.clientTimestamp,
          scenarioId: event.scenarioId,
          eventType: event.eventType,
          payload: event.payload
        }))
      }

      // Wait for transaction to complete
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(new Error('Snapshot apply transaction aborted'))
      })

    } catch (error) {
      console.error('Failed to apply scenario snapshot atomically:', error)
      throw error
    }
  }
}

export const localDB = new LocalDB()

// Re-export types for convenience
export type { ScenarioEvent } from '../core/events/types'
